import { AccountingDirectoryControlsComponent } from '../shared/accounting-directory-controls.component';
import { isEditableFinancialDraft } from '../shared/accounting-draft-eligibility';
import { Component, OnInit, inject } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { Router } from "@angular/router";
import { SessionStorageService } from "../../services/session-storage.service";
import { FinancePdfViewButtonComponent } from '../finance-pdf-view-button.component';
import { AccountsService } from "../accounts.service";
import { DebitNote } from "./debit-note.model";
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from "primeng/toast";
import { ProgressBarModule } from "primeng/progressbar";
import { TagModule } from "primeng/tag";
import { DrawerModule } from "primeng/drawer";
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { CompanyScopeSelection, CompanyScopeSelectorComponent } from '../company-scope-selector/company-scope-selector.component';
import { LocaleService } from '../../i18n/locale.service';
import { finalize } from 'rxjs';
import { PendingActionTracker } from '../../shared/state/pending-action-tracker';

@Component({
    selector: 'app-debit-note',
    templateUrl: './debit-note.component.html',
    styleUrls: ['../finance-list.shared.css', './debit-note.component.css'],
    standalone: true,
    imports: [FinancePdfViewButtonComponent,
        CommonModule,
        FormsModule,
        BreadcrumbModule,
        TableModule,
        ButtonModule,
        TooltipModule,
        ToastModule,
        ProgressBarModule,
        TagModule,
        DrawerModule,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageSearchPanelComponent,
        PageDataPanelComponent,
        AccountingDirectoryControlsComponent,
        CompanyScopeSelectorComponent
    ],
    providers: [
        AccountsService,
        MessageService,
        SessionStorageService
    ]
})
export class DebitNoteComponent implements OnInit {
    readonly pendingStatuses = ['DRAFT', 'SUBMITTED', 'APPROVED', 'ON_HOLD'];
    selectedDocumentStatuses: string[] = this.pendingStatuses.slice();
    searchText = '';
    directoryMode: 'LIST' | 'CRM' | 'PARTY_TYPE' = 'LIST';
    directoryFilters: Record<string, string | number | boolean | number[] | string[] | null | undefined> = {};
    private listRequestId = 0;

    readonly documentActions = new PendingActionTracker<number | undefined>();
    private readonly localeService = inject(LocaleService);

    breadcrumbItems: MenuItem[] | undefined;
    homeBreadcrumb: MenuItem | undefined;

    debitNotes: DebitNote[] = [];
    isLoading: boolean = false;
    first = 0;
    rows = 20;
    totalRecords = 0;

    canAddDebitNote = false;
    canEditDebitNote = false;
    previewDebitNote: DebitNote | null = null;
    isPreviewVisible = false;
    currentScope: CompanyScopeSelection | null = null;

    get isConsolidatedReadOnly(): boolean {
        return !!this.currentScope?.readOnly;
    }

    constructor(
        private debitNoteService: AccountsService,
        private messageService: MessageService,
        private router: Router,
        private sessionStorageService: SessionStorageService,
        private permissionService: PermissionService
    ) {
    }

    ngOnInit(): void {
        this.canAddDebitNote = this.permissionService.hasPermission(app_permissions.accounts.debit_note.create);
        this.canEditDebitNote = this.permissionService.hasPermission(app_permissions.accounts.debit_note.edit);

        this.breadcrumbItems = [
            { label: this.t('pages.debitNotes.breadcrumb.debitNote') }
        ];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };

        this.loadData();
    }

    onScopeChange(scope: CompanyScopeSelection) {
        this.currentScope = scope;
        this.applyFilters();
    }

    loadData(event?: any) {
        this.isLoading = true;
        const requestId = ++this.listRequestId;
        this.first = event?.first ?? 0;
        this.rows = event?.rows ?? 20;
        let page = event?.first ?? 0;
        let pageSize = event?.rows || 20;
        page = page / pageSize;
        const sessionCompanyId = this.sessionStorageService.getCompany()?.id;
        const companyGroupId = this.currentScope?.companyGroupId;
        const companyId = companyGroupId ? undefined : (this.currentScope?.companyId ?? sessionCompanyId);
        if (!companyId && !companyGroupId) {
            this.isLoading = false;
            return;
        }
        this.directoryFilters = { owningCompanyId: companyId, companyGroupId, searchText: this.searchText };

        this.debitNoteService.getAllDebitNotes(companyId, page, pageSize, companyGroupId, undefined, undefined, this.selectedDocumentStatuses, this.searchText).subscribe({
            next: (data) => {
                if (requestId !== this.listRequestId) return;
                this.debitNotes = data.items;
                this.totalRecords = data.count;
                this.isLoading = false;
            },
            error: (error) => {
                if (requestId !== this.listRequestId) return;
                this.messageService.add({
                    severity: 'error',
                    summary: this.t('common.error'),
                    detail: this.t('pages.debitNotes.messages.errorLoading')
                });
                this.isLoading = false;
            }
        });
    }

    addDebitNote() {
        if (this.isConsolidatedReadOnly) {
            this.messageService.add({ severity: 'warn', summary: this.t('pages.debitNotes.messages.readOnlyScopeSummary'), detail: this.t('pages.debitNotes.messages.addReadOnly') });
            return;
        }
        this.router.navigate(['/accounts/enterprise/receivables/debit-note/add']);
    }

    editDebitNote(debitNote: DebitNote) {
        if (this.isConsolidatedReadOnly) {
            this.messageService.add({ severity: 'warn', summary: this.t('pages.debitNotes.messages.readOnlyScopeSummary'), detail: this.t('pages.debitNotes.messages.editReadOnly') });
            return;
        }
        if (!this.isEditable(debitNote)) {
            this.messageService.add({
                severity: 'warn',
                summary: this.t('pages.debitNotes.messages.lockedDocumentSummary'),
                detail: this.t('pages.debitNotes.messages.lockedDocumentDetail')
            });
            return;
        }
        this.router.navigate(['/accounts/enterprise/receivables/debit-note/edit', debitNote.debitNoteID]);
    }

    viewDebitNote(debitNote: DebitNote) {
        if (debitNote.debitNoteID) {
            this.router.navigate(['/accounts/enterprise/receivables/debit-note/view', debitNote.debitNoteID]);
        }
    }

    onDirectoryModeChange(mode: 'LIST' | 'CRM' | 'PARTY_TYPE'): void {
        this.directoryMode = mode;
    }

    onDirectorySearch(value: string): void {
        this.searchText = value;
        this.applyFilters();
    }

    showPendingStatuses(): void {
        this.selectedDocumentStatuses = this.pendingStatuses.slice();
        this.applyFilters();
    }

    showAllStatuses(): void {
        this.selectedDocumentStatuses = [];
        this.applyFilters();
    }

    applyFilters() {
        this.loadData({ first: 0, rows: this.rows });
    }

    clearFilters() {
        this.selectedDocumentStatuses = this.pendingStatuses.slice();
        this.searchText = '';
        this.applyFilters();
    }

    get metrics(): PageMetric[] {
        this.localeService.locale();
        return [
            { label: this.t('pages.debitNotes.metrics.totalRecords'), value: this.totalRecords, hint: this.t('pages.debitNotes.metrics.backendCount') },
            { label: this.t('pages.debitNotes.metrics.visibleAmount'), value: this.formatMoney(this.getVisiblePageTotal()), hint: this.t('pages.debitNotes.metrics.visiblePageOnly'), tone: 'info' },
            { label: this.t('pages.debitNotes.metrics.posted'), value: this.countByDocumentStatus('POSTED'), hint: this.t('pages.debitNotes.metrics.visiblePageOnly'), tone: 'success' },
            { label: this.t('pages.debitNotes.metrics.draftOpen'), value: this.debitNotes.length - this.countByDocumentStatus('POSTED'), hint: this.t('pages.debitNotes.metrics.visiblePageOnly'), tone: 'warn' }
        ];
    }

    get resultText(): string {
        this.localeService.locale();
        return this.t('pages.debitNotes.results.debitNotes', { count: this.totalRecords });
    }

    get currentPageReportTemplate(): string {
        this.localeService.locale();
        return this.t('pages.debitNotes.table.pageReport');
    }

    get activeFilterCount(): number {
        return 0;
    }

    getVisiblePageTotal(): number {
        return this.debitNotes.reduce((total, debitNote) => total + this.getDebitNoteTotalAmountValue(debitNote), 0);
    }

    countByDocumentStatus(status: string): number {
        return this.debitNotes.filter(note => this.getDocumentStatus(note).toUpperCase() === status).length;
    }

    showDate(date: Date | undefined): string {
        if (date === null || date === undefined) return '';
        return date.toLocaleDateString();
    }

    getDebitNoteTotalAmount(debitNote: DebitNote) {
        return this.formatMoney(this.getDebitNoteTotalAmountValue(debitNote), 'SAR');
    }

    getDebitNoteTotalAmountValue(debitNote: DebitNote): number {
        if (debitNote.debitNoteAmount !== undefined && debitNote.debitNoteAmount !== null) {
            return debitNote.debitNoteAmount;
        }
        return debitNote.debitNoteLines?.reduce((total, line) => total + (line.amount || 0), 0) || 0;
    }

    getBeforeTaxAmount(debitNote: DebitNote): number | null {
        if (debitNote.debitNoteAmount != null) return debitNote.debitNoteAmount;
        const lines = debitNote.debitNoteLines;
        return lines?.length && lines.every(line => line.amount != null)
            ? lines.reduce((total, line) => total + (line.amount ?? 0), 0) : null;
    }

    getGrossAmount(debitNote: DebitNote): number | null {
        const beforeTax = this.getBeforeTaxAmount(debitNote);
        return beforeTax != null && debitNote.taxAmount != null ? beforeTax + debitNote.taxAmount : null;
    }

    formatKnownMoney(amount: number | null): string {
        return amount == null ? '—' : this.formatMoney(amount, 'SAR');
    }

    getDocumentStatus(debitNote: DebitNote): string {
        return debitNote.documentStatus || 'POSTED';
    }

    getPostingStatus(debitNote: DebitNote): string {
        return debitNote.postingStatus || 'POSTED';
    }

    getStatusSeverity(status: string | undefined): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
        switch ((status || '').toUpperCase()) {
            case 'POSTED':
            case 'APPROVED':
            case 'PAID':
                return 'success';
            case 'SUBMITTED':
            case 'DRAFT':
                return 'info';
            case 'ON_HOLD':
            case 'CANCELLED':
                return 'warning';
            case 'VOIDED':
            case 'REVERSED':
            case 'FAILED':
                return 'danger';
            default:
                return 'secondary';
        }
    }

    documentStatusLabel(status: string | undefined): string {
        this.localeService.locale();
        switch ((status || '').toUpperCase()) {
            case 'POSTED': return this.t('pages.debitNotes.status.posted');
            case 'APPROVED': return this.t('pages.debitNotes.status.approved');
            case 'PAID': return this.t('pages.debitNotes.status.paid');
            case 'SUBMITTED': return this.t('pages.debitNotes.status.submitted');
            case 'DRAFT': return this.t('pages.debitNotes.status.draft');
            case 'ON_HOLD': return this.t('pages.debitNotes.status.onHold');
            case 'CANCELLED': return this.t('pages.debitNotes.status.cancelled');
            case 'VOIDED': return this.t('pages.debitNotes.status.voided');
            case 'REVERSED': return this.t('pages.debitNotes.status.reversed');
            case 'FAILED': return this.t('pages.debitNotes.status.failed');
            default: return status || this.t('pages.debitNotes.status.unknown');
        }
    }

    isEditable(debitNote: DebitNote): boolean {
        return isEditableFinancialDraft(debitNote, this.sessionStorageService.getCompany()?.id);
    }

    formatMoney(amount: number | undefined, currencyCode: string = 'SAR'): string {
        return `${currencyCode || ''} ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
    }

    openPreview(debitNote: DebitNote) {
        this.previewDebitNote = debitNote;
        this.isPreviewVisible = true;
        if (!debitNote.debitNoteID) {
            return;
        }
        this.debitNoteService.getDebitNoteById(debitNote.debitNoteID).subscribe({
            next: note => this.previewDebitNote = note,
            error: () => this.previewDebitNote = debitNote
        });
    }

    downloadDebitNote(debitNote: DebitNote) {
        if (!debitNote.debitNoteID || !this.documentActions.begin(debitNote.debitNoteID)) return;
        this.debitNoteService.downloadDebitNoteDocument(debitNote.debitNoteID).pipe(
            finalize(() => this.documentActions.end(debitNote.debitNoteID))
        ).subscribe({
            next: pdf => this.debitNoteService.downloadPdfDocument(pdf, 'debit-note', debitNote.debitNoteNumber, debitNote.debitNoteID, debitNote.postingStatus),
            error: err => this.messageService.add({ severity: 'error', summary: 'Document unavailable', detail: err?.status === 404 ? 'Original unavailable: no stored debit note PDF exists.' : 'The debit note PDF is unavailable.' })
        });
    }

    t(key: string, fallbackOrParams?: string | Record<string, unknown>, params?: Record<string, unknown>): string {
        if (typeof fallbackOrParams === 'string') {
            return this.localeService.translateIfPresent(key, fallbackOrParams, params);
        }
        return this.localeService.translate(key, fallbackOrParams);
    }
}
